import React, { useState, useEffect } from 'react'
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { grey } from '@mui/material/colors';
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Link from "@mui/material/Link";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText"; 
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { useNavigate } from 'react-router-dom';
import { getLists, deleteList, createTeam, getTeams, deleteTeamMember } from '../../services/userService';
import { Chip, Box as MuiBox, Alert, Snackbar } from '@mui/material';

const TeamsList = () => {
  const [teams, setTeams] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [emailIds, setEmailIds] = useState('');
  const [emailList, setEmailList] = useState([]);
  const [currentEmail, setCurrentEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [selectedMemberName, setSelectedMemberName] = useState('');

  const navigate = useNavigate();

  const showAlert = (message, severity = 'info') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await getTeams();
        console.log('Teams response:', response.data);
        setTeams(response.data || []);
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };
    fetchTeams();
  }, []);

  
  const handleOpenDeleteDialog = (member) => {
    setSelectedMemberId(member._id || member.id);
    setSelectedMemberName(`${member.firstName || ''} ${member.lastName || ''}`);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedMemberId(null);
    setSelectedMemberName('');
  };


  const handleConfirmDelete = async () => {
    try {
    await deleteTeamMember(selectedMemberId);

      const teamsResponse = await getTeams();
      setTeams(teamsResponse.data || []);

      showAlert('Team member removed successfully', 'success');
    } catch (error) {
      console.error('Error deleting member:', error);
      showAlert('Failed to remove member', 'error');
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // const handleDeleteTeam = async (teamId) => {
  //   if (window.confirm('Are you sure you want to delete this team?')) {
  //     try {
  //       const response = await deleteTeam(teamId);        
  //       const teamsResponse = await getTeams();
  //       setTeams(teamsResponse.data || []);
  //     } catch (error) {
  //       console.error('Error deleting team:', error);
  //       alert('Failed to delete team. Please try again.');
  //     }
  //   }
  // };

  const handleListClick = (listId) => {
    navigate(`/team/${listId}`);
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setTeamName('');
    setTeamDescription('');
    setEmailIds('');
    setEmailList([]);
    setCurrentEmail('');
  };

  const handleAddEmail = () => {
    const email = currentEmail.trim();
    
    if (!email) {
      showAlert('Please enter an email address', 'warning');
      return;
    }
    
    if (!email.includes('@')) {
      showAlert('Please enter a valid email address (must contain @)', 'error');
      return;
    }
    
    if (emailList.includes(email)) {
      showAlert('This email address has already been added', 'warning');
      return;
    }
    
    setEmailList([...emailList, email]);
    setCurrentEmail('');
  };

  const handleRemoveEmail = (emailToRemove) => {
    setEmailList(emailList.filter(email => email !== emailToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleCreateTeam = async () => {
    if (emailList.length === 0) {
      showAlert('Please add at least one email address before inviting users', 'warning');
      return;
    }

    setLoading(true);
    try {
      const teamData = {
        emailIds: emailList
      };
      const response = await createTeam(teamData);
      const teamsResponse = await getTeams();
      setTeams(teamsResponse.data || []);
      
      showAlert('Users have been successfully invited to the team!', 'success');
      handleCloseDialog();
    } catch (error) {
      console.error('Error creating team:', error);
      showAlert('Failed to invite users. Please check your internet connection and try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#0a1128', borderRadius: 1, boxShadow: 2, p: 2  , mb:-2, width: '100%' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#eff7f6', mb: 2 }}>Team</Typography>
        <IconButton sx={{ color: '#eff7f6', mb: 2 }} onClick={handleOpenDialog}>
          <AddIcon />
        </IconButton>
      </Box>
      
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: grey[200] }}>
              <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Added On</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Action</TableCell> 
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No members found. Click the + icon to Add members.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              teams.map((team, index) => (
                <TableRow key={team._id || team.id} sx={{ '&:hover': { backgroundColor: grey[100] } }}>
                  <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{team.firstName} {team.lastName}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{team.email || 'No email'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {team.createdAt ? new Date(team.createdAt).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{team.firstName ? 'Yes' : 'No'}</TableCell>

                  <TableCell>
                    <IconButton color="error" onClick={() => handleOpenDeleteDialog(team)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>

                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Remove Team Member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove <strong>{selectedMemberName}</strong> from the team?
            <br /><br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Users to Team</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Enter Email Address"
            fullWidth
            variant="outlined"
            value={currentEmail}
            onChange={(e) => setCurrentEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="user@example.com"
            helperText="Type an email address and press Enter to add it"
            sx={{ mb: 2 }}
          />
          
          {emailList.length > 0 && (
            <MuiBox sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Added Emails ({emailList.length}):
              </Typography>
              <MuiBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {emailList.map((email, index) => (
                  <Chip
                    key={index}
                    label={email}
                    onDelete={() => handleRemoveEmail(email)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </MuiBox>
            </MuiBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading} sx={{ color: '#666' }}>Cancel</Button>
          <Button onClick={handleCreateTeam} variant="contained" disabled={loading} sx={{ bgcolor: '#1ce484','&:hover': { bgcolor: '#20ec84' },'&:disabled': { bgcolor: '#ccc' }}}>
            {loading ? 'Inviting...' : 'Invite Users'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alertSeverity} sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default TeamsList